import { useFetchPatientInfo } from '@/api/hooks/useFetchPatientInfo'
import { useUpdatePatientProfile } from '@/api/hooks/useUpdatePatientProfile'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { Input } from '@/ui/core/input'
import { HStack, VStack } from '@/ui/core/stack'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { Controller, useForm } from 'react-hook-form'
import Toast from 'react-native-toast-message'
import { check, minLength, object, pipe, string, trim } from 'valibot'
import validator from 'validator'
import type { ProfileFormData } from './ProfileInfoForm.types'

const ProfileSchema = object({
	firstName: pipe(string(), trim(), minLength(1, 'First name is required')),
	lastName: pipe(string(), trim(), minLength(1, 'Last name is required')),
	phone: pipe(
		string(),
		check(
			(value: string) => validator.isMobilePhone(value) as boolean,
			'Invalid phone number'
		)
	),
})

const ProfileInfoForm = () => {
	const { patientInfo } = useFetchPatientInfo()
	const { mutate: updatePatientInfo } = useUpdatePatientProfile()

	const {
		control,
		handleSubmit,
		formState: { isLoading, isValid },
	} = useForm<ProfileFormData>({
		resolver: valibotResolver(ProfileSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			phone: '',
		},
		values: patientInfo,
		resetOptions: {
			keepDirtyValues: true,
		},
	})

	const onFormSubmit = async ({ firstName, lastName, phone }: ProfileFormData) => {
		analytics.trackEvent('updating-profile')

		updatePatientInfo(
			{ firstName, lastName, phone },
			{
				onSuccess: () => {
					Toast.show({
						type: 'success',
						text1: 'Profile information updated!',
					})
				},
				onError: () => {
					Toast.show({
						type: 'error',
						text1: 'We ran into an issue updating your profile, please try again',
					})
				},
			}
		)
	}

	return (
		<>
			<VStack className="mb-20">
				<Header variant="h2" className="lg:text-5xl">
					Your profile.
				</Header>
			</VStack>
			<VStack gap={48}>
				<HStack fullWidth gap={32}>
					<Controller
						name="firstName"
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Input
								className="w-[45%]"
								placeholder="First name"
								value={value}
								onBlur={onBlur}
								onChangeText={onChange}
							/>
						)}
					/>
					<Controller
						name="lastName"
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Input
								className="w-[45%]"
								placeholder="Last name"
								value={value}
								onBlur={onBlur}
								onChangeText={onChange}
							/>
						)}
					/>
				</HStack>
				<Controller
					name="phone"
					control={control}
					render={({ field: { onChange, onBlur, value } }) => (
						<Input
							className="w-full"
							placeholder="Phone"
							value={value}
							onBlur={onBlur}
							onChangeText={onChange}
							inputMode="tel"
						/>
					)}
				/>
			</VStack>
			<VStack className="max-w-40 mt-20">
				<Button
					isLoading={isLoading}
					disabled={!isValid}
					onPress={handleSubmit(onFormSubmit)}
					withArrow
				>
					<ButtonText>Save</ButtonText>
				</Button>
			</VStack>
		</>
	)
}

export { ProfileInfoForm }
